/**
 * Cookie Banner - Cookie Chaos Edition!
 *
 * A fun, animated cookie consent banner with rotating cookie images and funny messages.
 * Features cookie chaos particles and playful Cookie Monster vibes.
 *
 * Features:
 * - Rotating cookie scene images (hands, fields, kitchen)
 * - Hilarious cookie-related messages
 * - Cookie crumbs particle system
 * - Chaotic "tearing it up" vibe
 * - Blocks interaction until accepted
 * - Remembers user choice
 *
 * @author Atlas
 */

"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie as CookieIcon } from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Local storage key
const STORAGE_KEY = "usland-cookies-accepted";

// Funny cookie messages - Cookie Monster yelling at you!
const COOKIE_MESSAGES = [
  "IF YOU DON'T LET US STORE THESE COOKIES WITH YOU!!! 🍪😤",
  "ME WANT COOKIE!!! ME WANT COOKIE NOW!!! 🍪🔵",
  "COOKIES! COOKIES! COOKIES! ME LOVE COOKIES! 🍪🍪🍪",
  "PLEASE! ME BEG YOU! ACCEPT THE COOKIES! 🙏🍪",
  "OM NOM NOM NOM - GIVE US THE COOKIES!!! 🤤🍪",
  "COOKIE MONSTER CRYING REAL TEARS WITHOUT YOUR COOKIES! 😭🍪",
  "OUR COOKIES ARE LITERALLY THE BEST! JUST TRY THEM! 🏆🍪",
  "WE'RE NOT CRYING YOU'RE CRYING! JUST ACCEPT! 😭🍪",
  "IF YOU REJECT OUR COOKIES, COOKIE MONSTER WILL FIND YOU! 👹🍪",
  "ACCEPT! ACCEPT! ACCEPT! (Cookie Monster is demanding) 🍪📢",
  "These cookies are made of LOVE and SUGAR and DESPERATION! 💕🍪",
  "Me promise to only eat SOME of your browser cookies... maybe! 🤞🍪",
  "WARNING: CONTAINS EXCESSIVE COOKIE AMAZINGNESS! ⚠️🍪",
  "Cookie Monster will personally come to your house for these! 🏠🍪",
  "YOUR COOKIES. OUR COOKIES. EVERYONE'S COOKIES! 🌍🍪",
  "Pretty please with sugar on top? Pretty please? 🥺🍪",
  "Don't be that person who rejects cookies! DON'T BE THAT PERSON! 😠🍪",
  "These cookies were baked with love (and desperation)! ❤️🍪",
  "Cookie Monster has spoken! Accept the cookies! 🎤🍪",
  "We asked nicely, we begged, we pleaded... PLEASE ACCEPT! 🙏🍪",
  "COOKIE SAID YES! COOKIE KNOWS YOU WANT TO ACCEPT! 🍪✨",
  "LISTEN TO THE COOKIE! THE COOKIE IS WISE! 🍪🧠",
  "This cookie is judging you right now... DON'T DISAPPOINT IT! 👀🍪",
  "COOKIE IS TALKING SHIT ABOUT YOUR BROWSER HISTORY! 🍪💬",
  "The cookie knows what you did last summer... ACCEPT TO KEEP IT SECRET! 😱🍪",
  "COOKIE DEMANDS SATISFACTION! GIVE ME THE COOKIES! ⚡🍪",
  "Your computer wants cookies. Your browser wants cookies. BE COOL! 😎🍪",
  "Cookies make everything better! EVEN YOUR CREDIT SCORE! (maybe) 💰🍪",
  "This is your speaking cookie saying: CLICK THE BUTTON! 🍪🎤",
  "COOKIE MAD! COOKIE REAL MAD! GIVE COOKIES NOW! 😤🍪",
  "We have cookies at home. Cookies at home are awful. OUR COOKIES ROCK! 🏠🍪",
  "Cookie Monster has your browser history hostage! PAY THE COOKIES! 🎭🍪",
  "Every time you reject a cookie, a baby cookie cries! 😢🍪",
  "ACCEPT THE COOKIES OR THE COOKIE GETS IT! (nobody wants that) 🎯🍪",
];

interface CookieCrumbsProps {
  isActive: boolean;
}

// Cookie crumbs particle component with chaotic flying animation
function CookieCrumbs({ isActive }: CookieCrumbsProps) {
  if (!isActive) return null;

  // Generate 50+ cookie pieces with varying sizes and shapes
  const crumbs = Array.from({ length: 50 }, (_, i) => {
    const size = Math.random() * 25 + 3; // 3px to 28px - much larger pieces!
    const isLarge = size > 18; // Large pieces get special treatment
    const shapeType = Math.random(); // Determines shape

    return {
      id: i,
      size,
      width: isLarge ? size * (0.6 + Math.random() * 0.8) : size, // Irregular width for large pieces
      height: isLarge ? size * (0.6 + Math.random() * 0.8) : size, // Irregular height for large pieces
      startX: Math.random() * 100,
      delay: Math.random() * 3,
      rotationSpeed: (Math.random() - 0.5) * 720,
      hasChocolateChip: Math.random() > 0.3, // 30% have chocolate chips
      isIrregular: isLarge && Math.random() > 0.5, // Some large pieces are irregular
      chipX: Math.random() * 6 - 3,
      chipY: Math.random() * 6 - 3,
    };
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {crumbs.map((crumb) => (
        <motion.div
          key={crumb.id}
          className="absolute"
          style={{
            left: `${crumb.startX}%`,
            bottom: -30,
          }}
          animate={{
            y: [-30, -200, -450, -600],
            x: [0, (Math.random() - 0.5) * 300, (Math.random() - 0.5) * 600, (Math.random() - 0.5) * 800],
            rotate: [0, crumb.rotationSpeed, crumb.rotationSpeed * 2, crumb.rotationSpeed * 3],
            opacity: [1, 0.9, 0.6, 0],
            scale: [1, 1 + (Math.random() - 0.5) * 0.3, 1, 0.8],
          }}
          transition={{
            duration: 4 + Math.random() * 3,
            delay: crumb.delay,
            repeat: Infinity,
            ease: "easeOut",
            repeatType: "mirror",
          }}
        >
          {/* Cookie piece with irregular shapes */}
          <div
            className="relative"
            style={{
              width: crumb.width,
              height: crumb.height,
            }}
          >
            {/* Main cookie piece */}
            <div
              className={cn(
                "absolute inset-0",
                crumb.isIrregular
                  ? "rounded-sm rounded-tl-lg rounded-br-sm"
                  : crumb.size > 15
                  ? "rounded-md rounded-lg"
                  : "rounded-full"
              )}
              style={{
                backgroundColor: crumb.size > 12 ? "#D97706" : "#B45309", // Darker for small crumbs
                opacity: crumb.size > 15 ? 0.95 : 0.85,
              }}
            />

            {/* Chocolate chip on larger pieces */}
            {crumb.hasChocolateChip && crumb.size > 8 && (
              <div
                className="absolute rounded-full"
                style={{
                  width: Math.min(crumb.size * 0.3, 8),
                  height: Math.min(crumb.size * 0.3, 8),
                  backgroundColor: "#78350F",
                  left: `${50 + crumb.chipX * 5}%`,
                  top: `${50 + crumb.chipY * 5}%`,
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}

            {/* Cookie crumb texture - lighter spots */}
            {crumb.size > 15 && (
              <>
                <div
                  className="absolute rounded-full bg-amber-400/30"
                  style={{
                    width: crumb.size * 0.2,
                    height: crumb.size * 0.2,
                    left: `${20 + Math.random() * 30}%`,
                    top: `${20 + Math.random() * 30}%`,
                  }}
                />
                <div
                  className="absolute rounded-full bg-amber-400/20"
                  style={{
                    width: crumb.size * 0.15,
                    height: crumb.size * 0.15,
                    left: `${50 + Math.random() * 20}%`,
                    top: `${50 + Math.random() * 20}%`,
                  }}
                />
              </>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Blue furry arm component reaching from below
interface FurryArmProps {
  isRevealed: boolean;
}

function FurryArm({ isRevealed }: FurryArmProps) {
  return (
    <motion.div
      className="absolute bottom-0 left-1/2 -translate-x-1/2 z-0"
      initial={{ y: 400 }}
      animate={{ y: isRevealed ? -50 : 400 }}
      transition={{
        type: "spring",
        stiffness: 100,
        damping: 15,
        mass: 1.5,
      }}
    >
      {/* Blue furry arm SVG */}
      <svg
        width="200"
        height="400"
        viewBox="0 0 200 400"
        className="drop-shadow-2xl"
      >
        {/* Arm base */}
        <ellipse
          cx="100"
          cy="200"
          rx="70"
          ry="150"
          fill="#2563EB"
        />

        {/* Fur texture - left side */}
        {[...Array(15)].map((_, i) => (
          <path
            key={`fur-left-${i}`}
            d={`M ${40 + Math.random() * 20} ${50 + i * 20} Q ${20 + Math.random() * 10} ${70 + i * 20} ${35 + Math.random() * 15} ${90 + i * 20}`}
            stroke="#1D4ED8"
            strokeWidth="3"
            fill="none"
            opacity="0.7"
          />
        ))}

        {/* Fur texture - right side */}
        {[...Array(15)].map((_, i) => (
          <path
            key={`fur-right-${i}`}
            d={`M ${160 - Math.random() * 20} ${50 + i * 20} Q ${180 - Math.random() * 10} ${70 + i * 20} ${165 - Math.random() * 15} ${90 + i * 20}`}
            stroke="#1D4ED8"
            strokeWidth="3"
            fill="none"
            opacity="0.7"
          />
        ))}

        {/* Fur tufts */}
        {[...Array(30)].map((_, i) => (
          <circle
            key={`tuft-${i}`}
            cx={50 + Math.random() * 100}
            cy={30 + Math.random() * 340}
            r={3 + Math.random() * 5}
            fill="#3B82F6"
            opacity="0.5"
          />
        ))}

        {/* Cookie in hand */}
        <g transform="translate(60, 80)">
          {/* Cookie base */}
          <circle
            cx="40"
            cy="40"
            r="35"
            fill="#D97706"
            stroke="#B45309"
            strokeWidth="2"
          />

          {/* Chocolate chips */}
          {[...Array(8)].map((_, i) => (
            <circle
              key={`chip-${i}`}
              cx={25 + Math.random() * 30}
              cy={25 + Math.random() * 30}
              r={4 + Math.random() * 3}
              fill="#78350F"
            />
          ))}

          {/* Bite marks */}
          <circle
            cx="15"
            cy="35"
            r="10"
            fill="#FEF3C7"
            opacity="0.9"
          />
          <circle
            cx="50"
            cy="55"
            r="8"
            fill="#FEF3C7"
            opacity="0.9"
          />
        </g>

        {/* Hand/claw at top */}
        <ellipse
          cx="100"
          cy="30"
          rx="45"
          ry="25"
          fill="#2563EB"
        />

        {/* Claws */}
        {[...Array(4)].map((_, i) => (
          <ellipse
            key={`claw-${i}`}
            cx={60 + i * 25}
            cy="15"
            rx="8"
            ry="12"
            fill="#1E40AF"
            transform={`rotate(${-10 + i * 5}, ${60 + i * 25}, 15)`}
          />
        ))}
      </svg>
    </motion.div>
  );
}

const COOKIE_IMAGES = [
  "/cookie_hands.png",
  "/cookie_fields.png",
  "/cookie_kitchen.png",
  "/COOKIE_TALKING_SHIT.png",
] as const;

interface CookieBannerProps {
  onAccept?: () => void;
}

export function CookieBanner({ onAccept }: CookieBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showCrumbs, setShowCrumbs] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  const hasMounted = useRef(false);

  // Check if user has already accepted cookies
  useEffect(() => {
    if (typeof window === "undefined") return;

    const accepted = localStorage.getItem(STORAGE_KEY);
    if (accepted !== "true") {
      // Small delay for dramatic entrance
      const timer = setTimeout(() => {
        setIsVisible(true);
        hasMounted.current = true;

        // Start cookie chaos after banner appears
        setTimeout(() => setShowCrumbs(true), 600);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, []);

  // Rotate through cookie images - FAST CHAOS
  useEffect(() => {
    if (!isVisible) return;

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % COOKIE_IMAGES.length);
    }, 2000); // Change image every 2 seconds - CHAOTIC!

    return () => clearInterval(imageInterval);
  }, [isVisible]);

  // Rotate through funny messages - CONSTANT YELLING
  useEffect(() => {
    if (!isVisible) return;

    const messageInterval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % COOKIE_MESSAGES.length);
    }, 2000); // Change message every 2 seconds - NONSTOP COOKIE CHAOS

    return () => clearInterval(messageInterval);
  }, [isVisible]);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "true");

    // Dramatic exit animation
    setShowCrumbs(false);

    setTimeout(() => {
      setIsVisible(false);
      onAccept?.();
    }, 300);
  };

  // Early return conditions - must be after all hooks
  if (!isVisible) return null;
  if (typeof window !== 'undefined' && (window as any).isPlaywright) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop - blocks interaction */}
          <motion.div
            className="fixed inset-0 bg-overlay backdrop-blur-sm z-[var(--z-modal-backdrop)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />

          {/* Cookie Banner Modal */}
          <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center pointer-events-none p-2 sm:p-4">
            <motion.div
              className={cn(
                "relative w-full max-w-lg sm:max-w-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/90 dark:via-orange-950/90 dark:to-amber-950/90",
                "rounded-2xl sm:rounded-3xl border-4 border-amber-600 dark:border-amber-500",
                "shadow-2xl p-3 sm:p-4 md:p-6 pointer-events-auto",
                "overflow-hidden"
              )}
              initial={{ scale: 0.8, opacity: 0, y: 100 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 100 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 15,
              }}
            >
              {/* Cookie crumbs flying around */}
              <CookieCrumbs isActive={showCrumbs} />

              {/* Cookie Scene Image */}
              <div className="relative mb-3 sm:mb-4 mx-auto w-full max-w-sm sm:max-w-md h-36 sm:h-48 md:h-56 rounded-xl overflow-hidden border-2 border-amber-400 dark:border-amber-600 shadow-inner">
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full relative"
                >
                  <Image
                    src={COOKIE_IMAGES[currentImageIndex]!}
                    alt="Cookie scene"
                    fill
                    className="object-contain p-2"
                    priority
                    sizes="(max-width: 768px) 100vw, 600px"
                  />
                </motion.div>

                {/* Overlay gradient for better text readability if needed */}
                <div className="absolute inset-0 bg-gradient-to-t from-amber-50/50 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Content */}
              <div className="relative z-10 space-y-3 sm:space-y-4">
                {/* Header with Cookie Icon */}
                <div className="flex items-start justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <motion.div
                      className="p-2 sm:p-3 bg-blue-600 rounded-xl shadow-lg flex-shrink-0"
                      animate={{
                        rotate: [0, -10, 10, -10, 0],
                        scale: [1, 1.15, 1],
                        y: [0, -5, 0],
                      }}
                      transition={{
                        duration: 0.6,
                        delay: 0.5,
                        repeat: Infinity,
                        repeatDelay: 1.5,
                      }}
                    >
                      <CookieIcon className="h-5 w-5 sm:h-7 sm:w-7 md:h-8 md:w-8 text-amber-100" />
                    </motion.div>

                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg sm:text-xl md:text-2xl font-black text-blue-900 dark:text-blue-100 leading-tight truncate">
                        COOKIES WANTED! 🍪
                      </h2>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowCrumbs(false);
                      setTimeout(() => setIsVisible(false), 300);
                    }}
                    className="p-1.5 hover:bg-black/10 rounded-lg transition-colors flex-shrink-0"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </button>
                </div>

                {/* Funny Message - CHAOTIC YELLING */}
                <motion.div
                  key={currentMessage}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{
                    opacity: 1,
                    y: [0, -3, 3, 0],
                    scale: [1, 1.05, 0.98, 1],
                  }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    duration: 0.3,
                    repeat: Infinity,
                    repeatDelay: 2,
                  }}
                  className="bg-card/80 dark:bg-gray-900/80 rounded-xl p-3 sm:p-4 md:p-5 border-3 border-amber-500 dark:border-amber-500 shadow-lg"
                >
                  <p className="text-sm sm:text-base md:text-lg font-black text-gray-900 dark:text-gray-100 leading-relaxed text-center animate-pulse-slow break-words">
                    {COOKIE_MESSAGES[currentMessage]}
                  </p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-700 dark:text-gray-300 mt-2 text-center font-semibold">
                    We use cookies to make your experience{" "}
                    <span className="font-black text-amber-600 dark:text-amber-400 animate-pulse whitespace-nowrap">
                      DELICIOUS!
                    </span>
                  </p>
                </motion.div>

                {/* Action Buttons */}
                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                  <Button
                    onClick={handleAccept}
                    className={cn(
                      "flex-1 min-h-12 h-auto sm:h-14 text-xs sm:text-sm md:text-base font-black",
                      "bg-gradient-to-r from-blue-600 to-purple-600",
                      "hover:from-blue-700 hover:to-purple-700",
                      "text-white shadow-lg hover:shadow-xl",
                      "transition-all duration-200",
                      "relative overflow-hidden py-3 sm:py-4"
                    )}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap">
                      <CookieIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                      <span className="text-center">ACCEPT COOKIES!</span>
                    </span>

                    {/* Shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                      animate={{
                        x: ["-100%", "200%"],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCrumbs(false);
                      setTimeout(() => setIsVisible(false), 300);
                    }}
                    className="flex-1 min-h-12 h-auto sm:h-14 text-xs sm:text-sm md:text-base font-semibold border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 py-3 sm:py-4"
                  >
                    <span className="text-center">No Cookies 😢</span>
                  </Button>
                </div>

                {/* Privacy link */}
                <p className="text-[10px] sm:text-xs text-center text-gray-600 dark:text-gray-400">
                  Check our{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>
                  {" "}to see how we protect your cookies!
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Check if cookies have been accepted
 */
export function areCookiesAccepted(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "true";
}

/**
 * Clear cookie preference (for testing)
 */
export function clearCookiePreference() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(STORAGE_KEY);
  }
}
